import { useEffect, useState } from 'react'
import { ArrowLeft, FilePen, Save, Trash2 } from 'lucide-react'
import { cmsApi } from '../lib/api'
import { newPlaceForm, placeFilled, placeFormOf, placeProblem, savePlaceForm, storedPlaceForm } from '../lib/catalog'
import { useCatalogDraft } from '../lib/useCatalogDraft'
import type { PlaceForm as Form, PlaceProfile, PlaceUsage, Producer } from '../types'
import { CatalogNotice } from './CatalogNotice'
import { LeaveDialog } from './LeaveDialog'
import { LoadingState } from './LoadingState'
import { PlaceForm } from './PlaceForm'
import { usageSummary } from './catalogForm'

type Props = {
  placeId: string | null
  // A new place's draft, continued here (PRD.md, item 98).
  draftId: string | null
  // Where the CMS's menu asks the editor before leaving it.
  exit: { current: ((go: () => void) => void) | null }
  producers: Producer[]
  canEdit: boolean
  catalogEditing: boolean
  onBack: () => void
  onSaved: (message: string) => void
  onError: (message: string) => void
  // A message that leaves the editor open.
  onNotice: (message: string) => void
  onOpenProfile: (producerId: string) => void
}

export function PlaceEditor({ placeId, draftId, exit, producers, canEdit, catalogEditing, onBack, onSaved, onError, onNotice, onOpenProfile }: Props) {
  const editable = canEdit && catalogEditing
  const [form, setForm] = useState<Form | null>(placeId || draftId ? null : newPlaceForm())
  // The place as it opened, to send only what changed and ask before losing it.
  const [saved, setSaved] = useState<Form | null>(null)
  const [usage, setUsage] = useState<PlaceUsage | null>(null)
  // The place's venue profile, its page in the app (@).
  const [profile, setProfile] = useState<PlaceProfile | null>(null)
  const [saving, setSaving] = useState(false)
  // A photo link typed and not yet prepared, or a file being prepared.
  const [imagePending, setImagePending] = useState(false)
  const [error, setError] = useState('')
  const [leaving, setLeaving] = useState<(() => void) | null>(null)
  const draft = useCatalogDraft({
    kind: 'place', draftId, value: form, filled: Boolean(form && placeFilled(form)), enabled: !placeId && editable,
    store: storedPlaceForm, onStored: (kept) => setForm((current) => current && current.profile.fields.image_url !== kept.profile.fields.image_url
      ? { ...current, profile: { ...current.profile, fields: { ...current.profile.fields, image_url: kept.profile.fields.image_url } } }
      : current),
  })

  useEffect(() => {
    let active = true
    if (placeId) {
      cmsApi.getPlace(placeId)
        .then((entry) => {
          if (!active) return
          const opened = placeFormOf(entry)
          setForm(opened)
          setSaved(opened)
          setUsage(entry.usage)
          setProfile(entry.profile ?? null)
        })
        .catch((requestError) => { if (active) setError(requestError instanceof Error ? requestError.message : 'Não foi possível abrir o lugar.') })
    } else if (draftId) {
      cmsApi.getCatalogDraft<Form>(draftId)
        .then((entry) => {
          if (!active) return
          draft.opened(entry.content, entry.updated_at)
          setForm(entry.content)
        })
        .catch((requestError) => { if (active) setError(requestError instanceof Error ? requestError.message : 'Não foi possível abrir o rascunho.') })
    }
    return () => { active = false }
  }, [placeId, draftId])

  // Leaving with something pending asks first, as in the event (PRD.md,
  // item 73): a saved place's changes, or a new one with something in it. A
  // draft opened from the list saves on leaving, as Gmail does.
  const changed = Boolean(form && saved && JSON.stringify(form) !== JSON.stringify(saved))
  const asksOnLeave = !form || !editable ? false
    : placeId ? changed
      : !draftId && (draft.id.current !== null || placeFilled(form))
  const leave = (go: () => void) => (asksOnLeave ? setLeaving(() => go) : go())
  useEffect(() => {
    exit.current = leave
    return () => { exit.current = null }
  })

  // Closing the tab with work not yet saved asks first.
  useEffect(() => {
    const waiting = form && editable && (placeId ? changed : placeFilled(form) && draft.behind(form))
    if (!waiting) return
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  })

  if (error) return <div className="error-state"><p>{error}</p><button className="button button--secondary" onClick={onBack}>Voltar</button></div>
  if (!form) return <LoadingState label="Abrindo lugar…" />

  const save = async () => {
    const problem = imagePending ? 'A foto ainda não foi preparada: toque em Buscar imagem e espere ficar pronta.' : placeProblem(form, Boolean(profile))
    if (problem) { onError(problem); return }
    setSaving(true)
    try {
      if (!placeId) await draft.close()
      const result = await savePlaceForm(form, { placeId, saved, profile, producers, draftId: draft.id.current })
      const madeProfile = result.profile && result.profile.id !== profile?.id ? result.profile : null
      onSaved(placeId
        ? madeProfile ? `Lugar atualizado, com o perfil @${madeProfile.handle}.` : 'Lugar atualizado.'
        : madeProfile ? `Lugar e perfil @${madeProfile.handle} criados.` : 'Lugar criado.')
    } catch (requestError) {
      draft.reopen()
      onError(requestError instanceof Error ? requestError.message : 'Não foi possível salvar o lugar.')
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!placeId || !window.confirm(`Excluir “${form.name}”? Essa ação não pode ser desfeita.`)) return
    setSaving(true)
    try {
      await cmsApi.deletePlace(placeId)
      onSaved('Lugar excluído.')
    } catch (requestError) {
      onError(requestError instanceof Error ? requestError.message : 'Não foi possível excluir o lugar.')
      setSaving(false)
    }
  }

  const deleteDraft = async (then: () => void) => {
    try {
      await draft.discard()
      if (draft.id.current) onNotice('Rascunho excluído.')
    } catch (requestError) {
      draft.reopen()
      onError(requestError instanceof Error ? requestError.message : 'Não foi possível excluir o rascunho.')
      return
    }
    then()
  }

  // Saved by the button, it goes back to Lugares, where it waits in Rascunhos.
  const saveDraftNow = async (then: () => void = onBack) => {
    try {
      await draft.saveNow(form)
    } catch (requestError) {
      onError(requestError instanceof Error ? requestError.message : 'Não foi possível salvar o rascunho.')
      return
    }
    onNotice('Rascunho salvo.')
    then()
  }

  const blocked = usage ? usageSummary(usage) : ''
  const disabled = !editable || saving
  const primary = <button className="button button--primary" disabled={saving} onClick={() => void save()}><Save size={18} /> {saving ? 'Salvando…' : placeId ? 'Salvar lugar' : 'Criar lugar'}</button>

  return (
    <div className="page-stack editor-page">
      {leaving && (
        <LeaveDialog
          kind={placeId ? 'changes' : 'draft'}
          entity="place"
          onDrop={placeId ? leaving : () => deleteDraft(leaving)}
          onKeep={() => saveDraftNow(leaving)}
          onClose={() => setLeaving(null)}
        />
      )}
      <section className="editor-heading">
        <button className="icon-button" onClick={() => leave(onBack)} aria-label="Voltar"><ArrowLeft size={20} /></button>
        <div><h1>{placeId ? form.name || 'Lugar' : 'Criar lugar'}</h1><p>Onde fica, o que oferece e as perguntas do público. A foto, a bio e os links ficam no perfil do lugar.</p></div>
        {editable && <div className="editor-heading__actions">
          {placeId && <button className="icon-button icon-button--danger" disabled={saving || Boolean(blocked)} title={blocked ? `Não pode ser excluído: ${blocked}.` : 'Excluir lugar'} onClick={() => void remove()} aria-label="Excluir lugar"><Trash2 size={18} /></button>}
          {primary}
        </div>}
      </section>

      <CatalogNotice catalogEditing={catalogEditing} />

      <PlaceForm
        value={form}
        onChange={setForm}
        disabled={disabled}
        profile={profile}
        producers={producers}
        onOpenProfile={(id) => leave(() => onOpenProfile(id))}
        onPendingImage={setImagePending}
        onError={onError}
      />

      {placeId && usage && <p className="catalog-usage">{blocked ? `Em uso: ${blocked}. Por isso não pode ser excluído.` : 'Nenhum evento, seguidor ou fonte de ingestão usa este lugar.'}</p>}

      {editable && (!placeId || changed) && (
        <div className="editor-footer" role="region" aria-label="Alterações não salvas">
          <span role="status">{placeId ? 'Alterações não salvas' : draft.label}</span>
          <div>
            {placeId
              ? <button className="button button--secondary" disabled={saving} onClick={() => { if (saved && window.confirm('Descartar as alterações não salvas? O lugar continua como está.')) setForm(saved) }}>Descartar alterações</button>
              : <>
                <button className="button button--secondary" disabled={saving} onClick={() => { if (window.confirm('Excluir este rascunho? Ele sai da lista de rascunhos.')) void deleteDraft(onBack) }}>Excluir rascunho</button>
                <button className="button button--secondary" disabled={saving || draft.state.status === 'saving'} onClick={() => void saveDraftNow()}><FilePen size={17} /> Salvar rascunho</button>
              </>}
            {primary}
          </div>
        </div>
      )}
    </div>
  )
}
